//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';

import { alpha, Box, Button, keyframes, Tooltip } from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';

// Draws a first look at a control nobody has touched yet -- a hover-red glow
// that grows and fades regardless of tier, so it reads the same whether the
// control underneath is showing its default or its open state.
const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${alpha('#f44336', 0.8)};
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 15px 5px ${alpha('#f44336', 0.6)};
    transform: scale(1.15);
  }
  100% {
    box-shadow: 0 0 0 0 ${alpha('#f44336', 0.2)};
    transform: scale(1);
  }
`;

// A control's lifecycle, drawn as escalating enclosure. Only panel launchers
// use all three; a plain control is always `ghost`.
export type HeaderControlTier = 'ghost' | 'outlined' | 'filled';

// A transparent border on the ghost tier rather than none, so a control does
// not shift by a pixel when it gains one.
const enclosureFor = (theme: Theme, tier: HeaderControlTier) => {
  if (tier === 'filled') return {
    color: theme.palette.menuPanel.selected.text,
    backgroundColor: theme.palette.menuPanel.selected.background,
    border: theme.border.default
  };
  if (tier === 'outlined') return {
    color: theme.palette.menuPanel.default.text,
    backgroundColor: 'transparent',
    border: theme.border.default
  };
  return {
    color: theme.palette.menuPanel.default.text,
    backgroundColor: 'transparent',
    border: '1px solid transparent'
  };
};

export interface HeaderControlProps {
  // A document-wide id on the button itself, for a control something outside
  // the header must find -- the panel host aims a dismissal's focus at the
  // launcher pill this way.
  id?: string;
  icon: React.ReactNode;
  // The short name under the glyph. Drawn unless the user asked for icons
  // alone: the header never sheds a label to make something fit, because a
  // bare glyph is a guess rather than a smaller affordance and it is worst
  // for the first-time user it is aimed at. Someone who chose icons has
  // already answered that, which is why the choice is theirs and not the
  // layout's.
  label: string;
  // The full phrasing, for the tooltip and the accessible description. The
  // label is short enough to stack, so this is where a control says what it
  // actually does.
  description?: string;
  // Overrides the accessible name where the visible label is too terse to
  // stand alone -- a `+2` overflow control, say. It must *contain* the label
  // verbatim, so that someone using speech control can say what they see.
  accessibleName?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  tier?: HeaderControlTier;
  // For a control that shows and hides something. `aria-pressed` rather than
  // `aria-expanded`, which belongs to a control that reveals a menu.
  isPressed?: boolean;
  isDisabled?: boolean;
  // Runs the hover-background pulse a control uses to draw a first look at
  // itself before anyone has touched it. Off unless a caller opts in.
  isPulsing?: boolean;
  // Skips HeaderControl's own tooltip for a caller that wraps this control
  // in one of its own -- Help's tooltip is controlled, staying shut while
  // its popper is open, which HeaderControl's uncontrolled tooltip cannot
  // express. Only the wrapping Tooltip is skipped: a `description` still
  // lands on the button's native `title`, so a caller pairing the two
  // should also drop `description` or it can still show a second tooltip.
  hasExternalTooltip?: boolean;
  // What the control reveals, when it reveals something: a menu of choices
  // or a panel of controls. The distinction is what a screen reader
  // announces before activation, so it has to match what actually opens.
  popup?: 'menu' | 'dialog';
  isExpanded?: boolean;
  // Draw the glyph alone. The caller decides: it knows both the user's
  // preference and whether this control has a glyph worth standing on.
  isLabelHidden?: boolean;
  // Draw the label alone, the titles counterpart of isLabelHidden. Unlike
  // that one it needs no guard -- every control has a label -- so a caller
  // passes the preference straight through. Never set both.
  isIconHidden?: boolean;
  // Put the label in the glyph's place and at the glyph's size, for a control
  // whose label is its whole identity rather than a name under a picture --
  // the `+N` count, which is the only one. Deliberately not implied by a
  // missing glyph: a pill whose panel has not registered its icon yet is
  // briefly iconless, and blowing its name up to glyph size would flash
  // something twice the width of its neighbors.
  isLabelGlyph?: boolean;
}

/**
 * One control in the header: a glyph with its name under it.
 *
 * Stacked rather than side by side because horizontal space is the scarce
 * axis -- the header holds context selectors, panel launchers, utilities and
 * an account menu on one line -- while a row of two-line controls costs a few
 * pixels of height once.
 *
 * The description sits on the button itself rather than on a wrapper. MUI
 * puts a tooltip's title on its immediate child, so a description on anything
 * but the element carrying the role never reaches the control a screen reader
 * stops at.
 */
const HeaderControl = React.forwardRef<HTMLButtonElement, HeaderControlProps>(
  ({
    id, icon, label, description, accessibleName, onClick, tier = 'ghost',
    isPressed, isDisabled = false, popup, isExpanded, isLabelHidden = false,
    isIconHidden = false, isLabelGlyph = false, isPulsing = false,
    hasExternalTooltip = false
  }, ref) => {
    const theme = useTheme();

    // With no visible text the button would otherwise have no accessible name
    // at all -- the failure that makes an icon-only header unusable by anyone
    // not looking at it.
    const spokenName = accessibleName ??
      (isLabelHidden ? label : undefined);

    // A hidden label makes the tooltip the only identification a sighted user
    // gets, and the native `title` attribute never appears on keyboard focus.
    // MUI's tooltip does, so it is what a glyph alone is given.
    const hint = description ?? label;

    const control = (
      <Button
        id={id}
        ref={ref}
        disabled={isDisabled}
        onClick={onClick}
        // Disabled restores the title a hidden label dropped: the tooltip
        // below opens from a wrapper then, so its description lands on the
        // wrapper and the button needs its own for assistive technology.
        title={isLabelHidden && !isDisabled ? undefined : description}
        aria-label={spokenName}
        aria-pressed={isPressed}
        aria-haspopup={popup}
        aria-expanded={popup ? Boolean(isExpanded) : undefined}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1px',
          padding: '3px 8px',
          minWidth: 0,
          flexShrink: 0,
          lineHeight: 1.1,
          borderRadius: theme.border.radius,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          ...enclosureFor(theme, tier),
          // Overrides the tier's own background rather than only tinting
          // whichever one was showing, so a pulsing control reads the same
          // whether it is closed or already open.
          ...(isPulsing && {
            backgroundColor: theme.palette.menuPanel.hover.background,
            animation: `${pulseAnimation} 2s infinite ease-in-out`
          }),
          '&:hover': {
            color: theme.palette.menuPanel.hover.text,
            backgroundColor: theme.palette.menuPanel.hover.background
          },
          // MUI's default disabled text is a translucent black, which the
          // dark header this sits in all but swallows.
          '&.Mui-disabled': {
            color: theme.palette.menuPanel.disabled.text,
            backgroundColor: theme.palette.menuPanel.disabled.background
          }
        }}
      >
        {!isLabelGlyph && !isIconHidden && (
          <Box
            aria-hidden
            sx={{ display: 'flex', fontSize: '20px', lineHeight: 1 }}
          >
            {icon}
          </Box>
        )}
        {!isLabelHidden && (
          <Box
            component="span"
            sx={{
              // The label's size says what it stands with: glyph-sized when
              // it stands in for the glyph, mid-sized when it is the
              // control's whole face, a caption under a glyph otherwise.
              fontSize: isLabelGlyph ? '20px' :
                isIconHidden ? '14px' : '11px',
              lineHeight: isLabelGlyph ? 1 : 1.1
            }}
          >
            {label}
          </Box>
        )}
      </Button>
    );

    if (hasExternalTooltip) return control;

    // A disabled button swallows pointer events, so neither its native title
    // nor a tooltip attached to it can ever open -- and the reason it is
    // disabled is the one thing a disabled control has to say. The span is
    // the wrapper MUI prescribes: it stays live under the pointer and fires
    // the tooltip on the button's behalf. Only where there is something
    // beyond the visible label to show, so a disabled labeled control with
    // no description does not grow a tooltip that repeats its own label.
    if (isDisabled && (isLabelHidden || description !== undefined)) {
      return (
        <Tooltip title={hint} describeChild>
          <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
            {control}
          </Box>
        </Tooltip>
      );
    }
    if (!isLabelHidden) return control;
    return (
      <Tooltip title={hint} describeChild>
        {control}
      </Tooltip>
    );
  });

HeaderControl.displayName = 'HeaderControl';

export default HeaderControl;
